import { getEntry } from '@helpers/content/content';
import {
  defaultAffinities,
  defaultCombatStats,
  defaultMonsterTypeDamageBonus,
  defaultStats,
  defaultTagResistances,
} from '@helpers/defaults';
import { itemPreviewDisplay } from '@helpers/item/item-preview';
import type {
  CombatStatBlock,
  ElementBlock,
  EquipmentContent,
  EquipmentItem,
  ItemPreviewDisplay,
  MonsterType,
  StatBlock,
  StatusEffectBlock,
} from '@interfaces';

function mergeBlock<T extends Record<string, number>>(
  base: T,
  bonus: T | undefined,
): T {
  const total = { ...base };
  (Object.keys(total) as (keyof T)[]).forEach((key) => {
    total[key] = (total[key] + (bonus?.[key] ?? 0)) as T[keyof T];
  });

  return total;
}

// Base+bonus totals, used as the OTHER column's comparison baseline in the equip-picker.
export function itemPreviewTotalStats(display: ItemPreviewDisplay): StatBlock {
  return mergeBlock(display.stats ?? defaultStats(), display.bonusStats);
}

export function itemPreviewTotalResistances(
  display: ItemPreviewDisplay,
): StatusEffectBlock {
  return mergeBlock(
    display.resistances ?? defaultTagResistances(),
    display.bonusResistances,
  );
}

export function itemPreviewTotalCombatStats(
  display: ItemPreviewDisplay,
): CombatStatBlock {
  return mergeBlock(
    display.combatStats ?? defaultCombatStats(),
    display.bonusCombatStats,
  );
}

export function itemPreviewTotalMonsterTypeDamage(
  display: ItemPreviewDisplay,
): Record<MonsterType, number> {
  return mergeBlock(
    display.monsterTypeDamage ?? defaultMonsterTypeDamageBonus(),
    display.bonusMonsterTypeDamage,
  );
}

export function itemPreviewTotalElementalResistances(
  display: ItemPreviewDisplay,
): ElementBlock {
  return mergeBlock(
    display.elementalResistances ?? defaultAffinities(),
    display.bonusElementalResistances,
  );
}

export function itemPreviewTotalElementalBoons(
  display: ItemPreviewDisplay,
): ElementBlock {
  return mergeBlock(
    display.elementalBoons ?? defaultAffinities(),
    display.bonusElementalBoons,
  );
}

export function equipmentItemPreviewDisplay(
  item: EquipmentItem,
): ItemPreviewDisplay | undefined {
  const content = getEntry<EquipmentContent>(item.equipmentId);
  return content ? itemPreviewDisplay('equipment', content, item) : undefined;
}
